import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Microphone, Stop, Trash, ArrowClockwise, WifiHigh, WifiSlash, Notebook, Sparkle, ArrowLeft } from "@phosphor-icons/react";
import { http } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { fpPath } from "@/lib/site";
import FarmersNav from "@/components/FarmersNav";
import SEOHead from "@/components/SEOHead";

// ------------------------------------------------------------
//  Tiny IndexedDB layer (no deps) — one store: voice-diary-queue
// ------------------------------------------------------------
const DB_NAME = "fp-voice-diary";
const DB_VERSION = 1;
const STORE = "queue";

function openDb() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: "client_id" });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}
async function idbAll() {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, "readonly");
    const req = tx.objectStore(STORE).getAll();
    req.onsuccess = () => resolve(req.result || []);
    req.onerror = () => reject(req.error);
  });
}
async function idbPut(entry) {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, "readwrite");
    tx.objectStore(STORE).put(entry);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}
async function idbDelete(client_id) {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, "readwrite");
    tx.objectStore(STORE).delete(client_id);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

// ------------------------------------------------------------
//  Web Speech API wrapper
// ------------------------------------------------------------
const SUPPORTED_LANGS = [
  ["en-US", "English (US)"], ["en-IN", "English (India)"], ["en-GB", "English (UK)"],
  ["hi-IN", "हिन्दी"], ["bn-IN", "বাংলা"], ["ta-IN", "தமிழ்"], ["te-IN", "తెలుగు"],
  ["mr-IN", "मराठी"], ["gu-IN", "ગુજરાતી"], ["ml-IN", "മലയാളം"], ["kn-IN", "ಕನ್ನಡ"],
  ["pa-IN", "ਪੰਜਾਬੀ"], ["ur-IN", "اردو"],
  ["es-ES", "Español"], ["fr-FR", "Français"], ["de-DE", "Deutsch"], ["pt-BR", "Português"],
  ["ar-SA", "العربية"], ["fa-IR", "فارسی"], ["tr-TR", "Türkçe"],
  ["zh-CN", "中文"], ["ja-JP", "日本語"], ["ko-KR", "한국어"],
  ["vi-VN", "Tiếng Việt"], ["th-TH", "ไทย"], ["id-ID", "Bahasa Indonesia"],
  ["sw-KE", "Kiswahili"], ["am-ET", "አማርኛ"],
];

function makeRecognizer(lang) {
  const R = window.SpeechRecognition || window.webkitSpeechRecognition;
  if (!R) return null;
  const rec = new R();
  rec.continuous = true;
  rec.interimResults = true;
  rec.lang = lang;
  return rec;
}

// ------------------------------------------------------------
//  Component
// ------------------------------------------------------------
export default function FarmersVoiceDiary() {
  const { user, openLogin } = useAuth();
  const [lang, setLang] = useState(() => localStorage.getItem("fp:voice-diary-lang") || "en-IN");
  const [state, setState] = useState("idle"); // idle | recording
  const [transcript, setTranscript] = useState("");
  const [interim, setInterim] = useState("");
  const [server, setServer] = useState([]);
  const [queued, setQueued] = useState([]);
  const [online, setOnline] = useState(typeof navigator !== "undefined" ? navigator.onLine : true);
  const [syncing, setSyncing] = useState(false);
  const [tagsInput, setTagsInput] = useState("");
  const recRef = useRef(null);
  const startedAtRef = useRef(null);

  useEffect(() => { localStorage.setItem("fp:voice-diary-lang", lang); }, [lang]);

  const canRecord = typeof window !== "undefined" && !!(window.SpeechRecognition || window.webkitSpeechRecognition);

  // -- Online/offline
  useEffect(() => {
    const on = () => setOnline(true), off = () => setOnline(false);
    window.addEventListener("online", on); window.addEventListener("offline", off);
    return () => { window.removeEventListener("online", on); window.removeEventListener("offline", off); };
  }, []);

  // -- Load server list + local queue
  const loadAll = useCallback(async () => {
    setQueued(await idbAll());
    if (user) {
      try {
        const { data } = await http.get("/farmers/voice-diary/entries?limit=100");
        setServer(data.entries || []);
      } catch { /* offline */ }
    }
  }, [user]);
  useEffect(() => { loadAll(); }, [loadAll]);

  // -- Sync queue → server whenever online + logged in
  const flushQueue = useCallback(async () => {
    if (!online || !user || syncing) return;
    const items = await idbAll();
    if (items.length === 0) return;
    setSyncing(true);
    let ok = 0, fail = 0;
    for (const it of items) {
      try {
        await http.post("/farmers/voice-diary/entries", it);
        await idbDelete(it.client_id);
        ok++;
      } catch (e) {
        fail++;
      }
    }
    setSyncing(false);
    if (ok) toast.success(`Synced ${ok} diary entr${ok === 1 ? "y" : "ies"}`);
    if (fail) toast.error(`${fail} entr${fail === 1 ? "y" : "ies"} failed to sync — will retry`);
    loadAll();
  }, [online, user, syncing, loadAll]);
  useEffect(() => { flushQueue(); }, [online, user, flushQueue]);

  // -- Recording
  const startRec = () => {
    if (!canRecord) { toast.error("Voice input isn't supported in this browser. Try Chrome or Safari."); return; }
    const rec = makeRecognizer(lang);
    if (!rec) return;
    setTranscript(""); setInterim("");
    startedAtRef.current = Date.now();
    let finalText = "";
    rec.onresult = (ev) => {
      let interimText = "";
      for (let i = ev.resultIndex; i < ev.results.length; i++) {
        const r = ev.results[i];
        if (r.isFinal) finalText += r[0].transcript + " ";
        else interimText += r[0].transcript;
      }
      setTranscript(finalText);
      setInterim(interimText);
    };
    rec.onerror = (e) => {
      if (e.error === "not-allowed") toast.error("Microphone permission denied.");
      else if (e.error !== "no-speech" && e.error !== "aborted") toast.error(`Speech error: ${e.error}`);
      setState("idle");
    };
    rec.onend = () => {
      // Some browsers cut off after silence — auto restart if user is still recording.
      if (recRef.current === rec && state === "recording") {
        try { rec.start(); } catch { /* noop */ }
      }
    };
    recRef.current = rec;
    setState("recording");
    try { rec.start(); } catch (e) { toast.error("Could not start microphone"); setState("idle"); }
  };
  const stopRec = () => {
    setState("idle");
    if (recRef.current) { try { recRef.current.stop(); } catch { /* noop */ } recRef.current = null; }
  };

  const combined = (transcript + " " + interim).trim();

  // -- Save
  const save = async () => {
    const text = combined;
    if (text.length < 2) { toast.error("Say something first."); return; }
    if (!user) { openLogin("voice-diary"); return; }
    const dur = startedAtRef.current ? Math.round((Date.now() - startedAtRef.current) / 1000) : null;
    const entry = {
      client_id: `vd_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
      transcript: text,
      language: lang,
      tags: tagsInput.split(",").map((t) => t.trim().toLowerCase()).filter(Boolean).slice(0, 8),
      duration_sec: dur,
      recorded_at: new Date().toISOString(),
    };
    // Always write to IDB first — guarantees offline durability
    await idbPut(entry);
    setTranscript(""); setInterim(""); setTagsInput("");
    if (online) {
      flushQueue();
    } else {
      toast.info("Saved offline — will sync when you're back online.");
      loadAll();
    }
  };

  const removeQueued = async (client_id) => {
    await idbDelete(client_id);
    loadAll();
  };
  const removeServer = async (id) => {
    try {
      await http.delete(`/farmers/voice-diary/entries/${id}`);
      loadAll();
    } catch { toast.error("Could not delete entry"); }
  };

  const allEntries = useMemo(() => {
    // Server rows already sorted; queued items always shown first
    return [
      ...queued.map((q) => ({ ...q, __queued: true })),
      ...server,
    ];
  }, [queued, server]);

  // --- Insights (this week) ---------------------------------------------
  // Group synced entries from the last 7 days by primary tag (or "untagged"),
  // producing a compact "running rhythm" view above the raw list.
  const insights = useMemo(() => {
    const now = Date.now();
    const weekMs = 7 * 24 * 60 * 60 * 1000;
    const cutoff = now - weekMs;
    const buckets = new Map();
    let total = 0, durationSec = 0;
    const languages = new Set();
    for (const e of allEntries) {
      const t = new Date(e.recorded_at || e.created_at).getTime();
      if (!Number.isFinite(t) || t < cutoff) continue;
      total += 1;
      durationSec += e.duration_sec || 0;
      if (e.language) languages.add(e.language.split("-")[0]);
      const key = ((e.tags || [])[0] || "untagged").toLowerCase();
      if (!buckets.has(key)) buckets.set(key, []);
      buckets.get(key).push(e);
    }
    // Sort buckets by count desc
    const groups = [...buckets.entries()]
      .map(([k, v]) => ({ tag: k, entries: v, count: v.length }))
      .sort((a, b) => b.count - a.count);
    return { total, durationSec, languages: [...languages], groups };
  }, [allEntries]);

  return (
    <main
      className="bg-[color:var(--np-bg,#F5EBDC)] text-[#151312] min-h-screen pt-32 md:pt-36 pb-32"
      data-testid="farmers-voice-diary-page"
    >
      <SEOHead
        title="Voice Diary · FarmersPulse"
        description="Talk to your farm diary. Voice-to-text notes in 28+ languages, offline-capable, sync when connected."
        path="/voice-diary"
      />
      <FarmersNav />

      <div className="max-w-[900px] mx-auto px-5 md:px-8 mt-6">
        <Link to={fpPath("/toolkit")} className="inline-flex items-center gap-1.5 font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[#3A3830] hover:text-[#151312]" data-testid="voice-diary-back">
          <ArrowLeft size={11} weight="bold" /> Back to toolkit
        </Link>

        <header className="mt-3">
          <div className="font-mono-tech text-[10px] tracking-[0.4em] uppercase text-[color:var(--np-brand,#B85F2A)] flex items-center gap-2">
            <Sparkle size={12} weight="fill" /> Tool 4 · Voice Diary
          </div>
          <h1 className="mt-2 font-serif-display text-4xl md:text-5xl leading-[0.98] tracking-tight">
            Talk to your farm diary
          </h1>
          <p className="mt-2 font-serif-display italic text-lg text-[#3A3830]">
            Hands muddy? Just speak. Voice-to-text in {SUPPORTED_LANGS.length}+ languages · works offline · syncs when you're back.
          </p>
        </header>

        {/* Status strip */}
        <div className="mt-6 flex items-center gap-2 flex-wrap font-mono-tech text-[10px] tracking-[0.24em] uppercase" data-testid="voice-diary-status">
          <span className={`inline-flex items-center gap-1 rounded-full border px-3 py-1.5 ${online ? "bg-emerald-50 border-emerald-200 text-emerald-800" : "bg-amber-50 border-amber-200 text-amber-900"}`}>
            {online ? <WifiHigh size={11} weight="fill" /> : <WifiSlash size={11} weight="fill" />}
            {online ? "Online" : "Offline — saving locally"}
          </span>
          <span className="rounded-full border border-black/15 bg-white px-3 py-1.5" data-testid="voice-diary-queued-count">
            Queued · {queued.length}
          </span>
          <span className="rounded-full border border-black/15 bg-white px-3 py-1.5" data-testid="voice-diary-server-count">
            Synced · {server.length}
          </span>
          {syncing && <span className="rounded-full bg-[#241209] text-[#F5EBDC] px-3 py-1.5"><ArrowClockwise size={11} weight="fill" className="inline animate-spin" /> Syncing…</span>}
          {!user && (
            <button onClick={() => openLogin("voice-diary-status")} className="rounded-full bg-[color:var(--np-brand,#B85F2A)] text-white px-3 py-1.5" data-testid="voice-diary-login">
              Sign in to sync
            </button>
          )}
        </div>

        {/* Recorder */}
        <section className="mt-6 rounded-3xl border border-black/10 bg-white p-6 md:p-8" data-testid="voice-diary-recorder">
          {/* Language + tags */}
          <div className="flex items-center gap-3 flex-wrap">
            <label className="font-mono-tech text-[10px] tracking-[0.24em] uppercase text-[#3A3830]">Language</label>
            <select
              value={lang}
              onChange={(e) => setLang(e.target.value)}
              disabled={state === "recording"}
              data-testid="voice-diary-lang"
              className="rounded-full border border-black/15 bg-white px-3 py-1.5 text-sm"
            >
              {SUPPORTED_LANGS.map(([code, label]) => (
                <option key={code} value={code}>{label}</option>
              ))}
            </select>
            <input
              value={tagsInput}
              onChange={(e) => setTagsInput(e.target.value)}
              placeholder="tags · comma separated (planting, pest, harvest…)"
              data-testid="voice-diary-tags"
              className="flex-1 min-w-[200px] rounded-full border border-black/15 bg-white px-4 py-1.5 text-sm placeholder:text-[#8A8479]"
            />
          </div>

          {/* Big mic */}
          <div className="mt-6 flex flex-col items-center">
            {state === "idle" ? (
              <button
                onClick={startRec}
                disabled={!canRecord}
                data-testid="voice-diary-start"
                className="group rounded-full bg-[#B85F2A] text-white p-10 md:p-14 shadow-lg hover:opacity-90 transition disabled:opacity-40"
                aria-label="Start recording"
              >
                <Microphone size={48} weight="fill" />
              </button>
            ) : (
              <button
                onClick={stopRec}
                data-testid="voice-diary-stop"
                className="group rounded-full bg-[#241209] text-[#F5EBDC] p-10 md:p-14 shadow-lg hover:opacity-90 transition animate-pulse"
                aria-label="Stop recording"
              >
                <Stop size={48} weight="fill" />
              </button>
            )}
            <div className="mt-3 font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[#3A3830]" data-testid="voice-diary-state">
              {!canRecord ? "Voice not supported in this browser" : state === "recording" ? "Listening — tap to stop" : "Tap to speak"}
            </div>
          </div>

          {/* Live transcript */}
          {(transcript || interim) && (
            <div className="mt-6" data-testid="voice-diary-transcript-block">
              <div className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[color:var(--np-brand,#B85F2A)]">Transcript</div>
              <div className="mt-2 rounded-2xl border border-black/10 bg-[#FBF2E1] p-4 min-h-[100px] font-serif-display text-lg leading-relaxed" data-testid="voice-diary-transcript">
                <span>{transcript}</span>
                {interim && <span className="text-[#8A8479] italic"> {interim}</span>}
              </div>
              <textarea
                className="mt-2 w-full rounded-2xl border border-black/10 bg-white p-3 text-sm min-h-[80px]"
                placeholder="Edit before saving if you wish…"
                value={transcript}
                onChange={(e) => { setTranscript(e.target.value); setInterim(""); }}
                data-testid="voice-diary-edit"
              />
              <div className="mt-3 flex gap-2 flex-wrap">
                <button
                  onClick={save}
                  disabled={combined.length < 2}
                  data-testid="voice-diary-save"
                  className="rounded-full bg-[color:var(--np-brand,#B85F2A)] text-white px-5 py-2 font-mono-tech text-[10px] tracking-[0.28em] uppercase hover:opacity-90 disabled:opacity-40"
                >
                  <Notebook size={11} weight="fill" className="inline mr-1" />
                  Save to diary
                </button>
                <button
                  onClick={() => { setTranscript(""); setInterim(""); }}
                  data-testid="voice-diary-clear"
                  className="rounded-full border border-black/15 bg-white px-4 py-2 font-mono-tech text-[10px] tracking-[0.28em] uppercase hover:bg-black/5"
                >
                  Clear
                </button>
              </div>
            </div>
          )}
        </section>

        {/* Weekly insights */}
        {insights.total > 0 && (
          <section className="mt-8 rounded-3xl border border-black/10 bg-[#FBF2E1] p-5 md:p-6" data-testid="voice-diary-insights">
            <div className="flex items-center gap-2 font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[color:var(--np-brand,#B85F2A)]">
              <Sparkle size={12} weight="fill" /> This week's rhythm
            </div>
            <h2 className="mt-1 font-serif-display text-2xl md:text-3xl">
              {insights.total} note{insights.total === 1 ? "" : "s"} · {Math.round(insights.durationSec / 60)} min recorded · {insights.languages.length} language{insights.languages.length === 1 ? "" : "s"}
            </h2>
            <div className="mt-4 flex flex-wrap gap-2">
              {insights.groups.map((g) => (
                <a
                  key={g.tag}
                  href={`#tag-${g.tag}`}
                  data-testid={`voice-diary-insight-tag-${g.tag}`}
                  className="rounded-full border border-black/15 bg-white px-3 py-1.5 font-mono-tech text-[10px] tracking-[0.28em] uppercase hover:bg-black/5"
                >
                  #{g.tag} · {g.count}
                </a>
              ))}
            </div>
            <ul className="mt-4 space-y-2">
              {insights.groups.slice(0, 6).map((g) => (
                <li key={g.tag} id={`tag-${g.tag}`} className="text-sm">
                  <span className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[#3A3830]">#{g.tag}</span>
                  <span className="ml-2 text-[#3A3830]">
                    {g.entries
                      .slice(0, 2)
                      .map((e) => (e.transcript || "").split(" ").slice(0, 12).join(" ") + "…")
                      .join("  ·  ")}
                  </span>
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* Entries */}
        <section className="mt-10" data-testid="voice-diary-list">
          <div className="flex items-center gap-3 flex-wrap">
            <h2 className="font-serif-display text-2xl md:text-3xl">Your diary</h2>
            <button onClick={flushQueue} disabled={!online || !user || queued.length === 0} className="ml-auto rounded-full border border-black/15 bg-white px-3 py-1.5 font-mono-tech text-[10px] tracking-[0.28em] uppercase disabled:opacity-40" data-testid="voice-diary-flush">
              <ArrowClockwise size={11} weight="fill" className="inline mr-1" /> Sync now
            </button>
          </div>

          {allEntries.length === 0 ? (
            <div className="mt-4 rounded-2xl border border-dashed border-black/15 bg-white/60 p-8 text-center text-sm text-[#3A3830]" data-testid="voice-diary-empty">
              No entries yet. Tap the mic and speak — your first note lands here.
            </div>
          ) : (
            <ul className="mt-4 space-y-3">
              {allEntries.map((e) => (
                <li
                  key={e.client_id || e.id}
                  data-testid={`voice-diary-entry-${e.client_id || e.id}`}
                  className={`rounded-2xl border p-4 md:p-5 ${e.__queued ? "border-amber-300 bg-amber-50" : "border-black/10 bg-white"}`}
                >
                  <div className="flex items-center gap-2 flex-wrap font-mono-tech text-[10px] tracking-[0.24em] uppercase text-[#3A3830]">
                    <span>{new Date(e.recorded_at || e.created_at).toLocaleString()}</span>
                    <span>· {e.language}</span>
                    {e.duration_sec ? <span>· {e.duration_sec}s</span> : null}
                    {e.__queued ? <span className="text-amber-700">· Pending sync</span> : <span className="text-emerald-800">· Synced</span>}
                    <button
                      onClick={() => e.__queued ? removeQueued(e.client_id) : removeServer(e.id)}
                      className="ml-auto rounded-full border border-black/15 bg-white p-1.5 hover:bg-red-50"
                      aria-label="Delete"
                      data-testid={`voice-diary-delete-${e.client_id || e.id}`}
                    >
                      <Trash size={12} weight="fill" />
                    </button>
                  </div>
                  <p className="mt-2 font-serif-display text-base leading-relaxed whitespace-pre-wrap">{e.transcript}</p>
                  {(e.tags || []).length > 0 && (
                    <div className="mt-2 flex gap-1 flex-wrap">
                      {e.tags.map((t) => <span key={t} className="rounded-full bg-[#FBF2E1] border border-black/10 px-2 py-0.5 font-mono-tech text-[9px] tracking-[0.22em] uppercase">#{t}</span>)}
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}
        </section>

        <p className="mt-8 text-[10px] font-mono-tech tracking-[0.22em] uppercase text-[#3A3830]">
          Privacy · Audio never leaves your browser. Only the transcript is sent to FarmersPulse when you're signed in. Offline notes are stored in your browser's IndexedDB until you sync.
        </p>
      </div>
    </main>
  );
}
